import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Gift,
  Heart,
  Mail,
  Music,
  Paintbrush,
  Palette,
  PlayCircle,
  ShoppingBag,
  Smile,
  Sparkles,
  Star,
  Sun,
  Wand2,
} from "lucide-react";

const YOUTUBE_URL = "https://www.youtube.com/@tantalizingart";

const InstagramIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const fadeInUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const featuredPaintings = [
  {
    name: "David Attenborough",
    birthday: "Saturday 8 May",
    image: "/DavidAttenborough.png",
  },
  {
    name: "Adele",
    birthday: "Thursday 5 May",
    image: "/Adele.png",
  },
  {
    name: "Dua Lipa",
    birthday: "Tuesday 22 August",
    image: "/DuaLipa.png",
  },
];

const experiences = [
  {
    title: "Birthday Meaning Paintings",
    description:
      "Turn a birth date into a custom artwork inspired by weekday energy, numerology, month symbolism, colour, and personal meaning.",
    icon: Calendar,
    linkText: "Order your birthday painting",
    to: "/birthday-meaning-paintings",
    accent: "turquoise",
  },
  {
    title: "Daily Affirmations",
    description:
      "Start your morning with poetic affirmations, symbolic energy, gentle humour, and visual art designed to make the day feel less mechanical.",
    icon: PlayCircle,
    linkText: "Watch on YouTube",
    href: YOUTUBE_URL,
    accent: "gold",
  },
  {
    title: "Music Visualizer",
    description:
      "Upload music and see it become movement, shape, pulse, and atmosphere. A little theatre for sound.",
    icon: Music,
    linkText: "Experience the visualizer",
    to: "/music-visualizer",
    accent: "violet",
  },
  {
    title: "Mirror of Cheer",
    description:
      "A playful AI experience designed to lift the mood with humour, expression, and a bright little spark of absurd joy.",
    icon: Smile,
    linkText: "Open the mirror",
    to: "/mirror-of-cheer",
    accent: "pink",
  },
];

const howItWorks = [
  {
    title: "You give us a date",
    text: "For birthday paintings, the full date helps reveal the weekday, number energy, season, and symbolic foundation.",
  },
  {
    title: "We shape the meaning",
    text: "The artwork is built around colours, symbols, mood, and story, not just a generic birthday template.",
  },
  {
    title: "You receive personal art",
    text: "A meaningful piece for yourself, a partner, a parent, a friend, or anyone who deserves more than another ordinary gift.",
  },
];

const getAccentClasses = (accent) => {
  switch (accent) {
    case "gold":
      return {
        icon: "text-[#C79A43]",
        iconBg: "bg-[#C79A43]/10 border-[#C79A43]/20",
        border: "hover:border-[#C79A43]/45",
        link: "text-[#C79A43] hover:text-white",
      };
    case "violet":
      return {
        icon: "text-violet-400",
        iconBg: "bg-violet-500/10 border-violet-500/20",
        border: "hover:border-violet-500/45",
        link: "text-violet-300 hover:text-white",
      };
    case "pink":
      return {
        icon: "text-pink-400",
        iconBg: "bg-pink-500/10 border-pink-500/20",
        border: "hover:border-pink-500/45",
        link: "text-pink-300 hover:text-white",
      };
    case "turquoise":
    default:
      return {
        icon: "text-[#00B4A6]",
        iconBg: "bg-[#00B4A6]/10 border-[#00B4A6]/20",
        border: "hover:border-[#00B4A6]/45",
        link: "text-[#00B4A6] hover:text-white",
      };
  }
};

export default function LandingPage() {
  useEffect(() => {
    document.title =
      "Tantalizing.Art | Birthday Meaning Paintings & Creative Energy Art";

    const updateMetaTag = (name, content) => {
      let tag =
        document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(name.includes("og:") ? "property" : "name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    updateMetaTag(
      "description",
      "Tantalizing.Art creates birthday meaning paintings, daily energy videos, and playful creative AI experiences inspired by symbolism, colour, and personal meaning."
    );
    updateMetaTag(
      "keywords",
      "Tantalizing Art, birthday meaning painting, custom birthday art, personalised art, numerology painting, spiritual art, daily affirmations, AI art"
    );
    updateMetaTag(
      "og:title",
      "Tantalizing.Art | Birthday Meaning Paintings"
    );
    updateMetaTag(
      "og:description",
      "Turn your birthday into a custom artwork inspired by meaning, colour, symbolism, and energy."
    );
    updateMetaTag("og:type", "website");
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#00B4A6] selection:text-white font-sans overflow-hidden">
      {/* Static artistic background. No animated template effect. */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-18rem] left-[-12rem] w-[42rem] h-[42rem] rounded-full bg-[#00B4A6]/12 blur-[140px]" />
        <div className="absolute top-[10rem] right-[-16rem] w-[36rem] h-[36rem] rounded-full bg-[#C79A43]/12 blur-[140px]" />
        <div className="absolute bottom-[-18rem] left-[20%] w-[36rem] h-[36rem] rounded-full bg-violet-700/10 blur-[150px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.04),transparent_28%,rgba(255,255,255,0.03))]" />
      </div>

      <main className="relative z-10">
        {/* Hero */}
        <section className="px-6 pt-24 pb-20 md:pt-28 md:pb-28">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div
                variants={fadeInUp}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00B4A6]/30 bg-[#00B4A6]/10 mb-8"
              >
                <Sparkles className="w-4 h-4 text-[#00B4A6]" />
                <span className="text-xs font-black tracking-widest uppercase text-[#DDFBF8]">
                  Personal art with meaning
                </span>
              </motion.div>

              <motion.h1
                variants={fadeInUp}
                className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[1.03] mb-7"
              >
                Art that knows
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00B4A6] via-[#DDFBF8] to-[#C79A43]">
                  why it exists.
                </span>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl text-zinc-400 leading-relaxed max-w-2xl mb-9"
              >
                Tantalizing.Art creates symbolic, personalised digital and
                physical art experiences. Our main piece is the custom birthday
                meaning painting: your birth date translated into colour,
                energy, story, and visual poetry.
              </motion.p>

              <motion.div
                variants={fadeInUp}
                className="flex flex-col sm:flex-row flex-wrap gap-4"
              >
                <Link
                  to="/birthday-meaning-paintings"
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#00B4A6] hover:bg-[#00796B] text-white rounded-full font-black transition-all duration-300 shadow-[0_0_26px_rgba(0,180,166,0.25)] hover:shadow-[0_0_38px_rgba(0,180,166,0.45)] hover:-translate-y-1"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Buy Birthday Painting
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>

                <a
                  href={YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#C79A43]/50 text-white rounded-full font-black transition-all duration-300 hover:-translate-y-1"
                >
                  <PlayCircle className="w-5 h-5 text-[#C79A43]" />
                  Watch Daily Affirmations
                </a>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="grid sm:grid-cols-3 gap-3 max-w-2xl mt-10"
              >
                {[
                  "Custom birthday art",
                  "Symbolic storytelling",
                  "Gifts with meaning",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3"
                  >
                    <CheckCircle2 className="w-4 h-4 text-[#00B4A6] shrink-0" />
                    <span className="text-sm font-semibold text-zinc-300">
                      {item}
                    </span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Hero visual card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.85, ease: "easeOut", delay: 0.1 }}
              className="relative"
            >
              <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-[#00B4A6]/20 via-transparent to-[#C79A43]/20 blur-3xl" />

              <div className="relative rounded-[2rem] border border-white/10 bg-zinc-950/70 backdrop-blur-xl p-5 md:p-6 shadow-2xl">
                <div className="rounded-[1.6rem] border border-white/10 bg-black/60 p-4 mb-5">
                  <div className="grid grid-cols-3 gap-3">
                    {featuredPaintings.map((item, index) => (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 + index * 0.1 }}
                        className="rounded-2xl overflow-hidden border border-white/10 bg-black"
                      >
                        <div className="aspect-[3/4] p-2">
                          <img
                            src={item.image}
                            alt={`${item.name} birthday meaning painting`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#00B4A6]/10 border border-[#00B4A6]/25 flex items-center justify-center">
                    <Paintbrush className="w-8 h-8 text-[#00B4A6]" />
                  </div>

                  <div>
                    <p className="text-xs font-black tracking-widest uppercase text-[#C79A43] mb-1">
                      Featured service
                    </p>
                    <h2 className="text-2xl md:text-3xl font-black text-white">
                      Birthday Meaning Paintings
                    </h2>
                    <p className="text-zinc-400 mt-2 leading-relaxed">
                      A custom artwork created from your birthday, colours,
                      symbols, and personal story.
                    </p>
                  </div>
                </div>

                <div className="mt-6 grid sm:grid-cols-3 gap-3">
                  {featuredPaintings.map((item) => (
                    <div
                      key={item.name}
                      className="rounded-2xl bg-white/[0.04] border border-white/10 p-4"
                    >
                      <p className="text-[11px] uppercase tracking-widest text-[#C79A43] font-black">
                        Birthday
                      </p>
                      <p className="text-sm font-black text-white mt-1">
                        {item.birthday}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">{item.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Birthday art spotlight */}
        <section
          id="birthday-art"
          className="px-6 py-20 border-y border-white/5 bg-white/[0.035]"
        >
          <div className="max-w-7xl mx-auto grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeInUp}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C79A43]/30 bg-[#C79A43]/10 mb-7">
                <Gift className="w-4 h-4 text-[#C79A43]" />
                <span className="text-xs font-black tracking-widest uppercase text-[#F4E7A1]">
                  A better birthday gift
                </span>
              </div>

              <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-tight mb-6">
                Not another random present.
                <span className="block text-zinc-500">
                  A date turned into a story.
                </span>
              </h2>

              <p className="text-lg text-zinc-400 leading-relaxed mb-8">
                A birthday meaning painting is built from the person’s birth
                date, weekday, number, month, colours, and chosen symbols. It
                becomes a gift with a private engine inside it. Quietly
                personal. Visually beautiful. Impossible to buy from a shelf.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/birthday-meaning-paintings"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#C79A43] hover:bg-[#a87f34] text-black font-black transition-all duration-300"
                >
                  <Calendar className="w-5 h-5" />
                  Explore Birthday Paintings
                </Link>

                <a
                  href="mailto:tartalizing@gmail.com?subject=Birthday%20Meaning%20Painting%20Enquiry"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white font-black transition-all duration-300"
                >
                  <Mail className="w-5 h-5" />
                  Email Us
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.75 }}
              className="grid md:grid-cols-2 gap-5"
            >
              <div className="rounded-[2rem] border border-white/10 bg-black/45 p-6">
                <Palette className="w-10 h-10 text-[#00B4A6] mb-5" />
                <h3 className="text-2xl font-black mb-3">
                  Created from meaning
                </h3>
                <p className="text-zinc-400 leading-relaxed">
                  Weekday energy, number symbolism, month atmosphere, favourite
                  colours, personal symbols, and story all help shape the final
                  artwork.
                </p>
              </div>

              <div className="rounded-[2rem] border border-white/10 bg-black/45 p-6">
                <Heart className="w-10 h-10 text-[#C79A43] mb-5" />
                <h3 className="text-2xl font-black mb-3">
                  Designed to feel personal
                </h3>
                <p className="text-zinc-400 leading-relaxed">
                  It can be romantic, spiritual, joyful, calm, bold, poetic, or
                  mysterious. The painting follows the person, not the trend.
                </p>
              </div>

              <div className="md:col-span-2 rounded-[2rem] border border-[#00B4A6]/20 bg-[#00B4A6]/10 p-6">
                <div className="flex flex-col md:flex-row md:items-center gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-black/35 border border-white/10 flex items-center justify-center shrink-0">
                    <Star className="w-7 h-7 text-[#F4E7A1]" />
                  </div>

                  <div>
                    <h3 className="text-2xl font-black mb-2">
                      Available from £10
                    </h3>
                    <p className="text-zinc-300 leading-relaxed">
                      A4 birthday meaning painting £10. A3 birthday meaning
                      painting £15. Simple, meaningful, and made to be gifted.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Experiences */}
        <section id="experiences" className="px-6 py-24">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeInUp}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.04] mb-6">
                <Wand2 className="w-4 h-4 text-[#00B4A6]" />
                <span className="text-xs font-black tracking-widest uppercase text-zinc-300">
                  Creative experiences
                </span>
              </div>

              <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-5">
                A small world of art, energy, and playful AI
              </h2>

              <p className="text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
                Meaningful creative pages that make people want to click
                deeper, without heavy template effects or glitchy digital fog.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {experiences.map((item, index) => {
                const Icon = item.icon;
                const accent = getAccentClasses(item.accent);

                const content = (
                  <>
                    <div
                      className={`w-14 h-14 rounded-2xl border flex items-center justify-center mb-7 ${accent.iconBg}`}
                    >
                      <Icon className={`w-7 h-7 ${accent.icon}`} />
                    </div>

                    <h3 className="text-2xl md:text-3xl font-black text-white mb-4">
                      {item.title}
                    </h3>

                    <p className="text-zinc-400 leading-relaxed text-lg mb-7">
                      {item.description}
                    </p>

                    <div
                      className={`inline-flex items-center gap-2 font-black transition-all duration-300 group-hover:gap-3 ${accent.link}`}
                    >
                      {item.linkText}
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </>
                );

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.65, delay: index * 0.08 }}
                  >
                    {item.href ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`group block h-full rounded-[2rem] border border-white/10 bg-zinc-950/55 p-8 md:p-9 transition-all duration-500 hover:-translate-y-1 ${accent.border}`}
                      >
                        {content}
                      </a>
                    ) : (
                      <Link
                        to={item.to}
                        className={`group block h-full rounded-[2rem] border border-white/10 bg-zinc-950/55 p-8 md:p-9 transition-all duration-500 hover:-translate-y-1 ${accent.border}`}
                      >
                        {content}
                      </Link>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section
          id="how-it-works"
          className="px-6 py-24 border-y border-white/5 bg-white/[0.035]"
        >
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeInUp}
              className="text-center mb-14"
            >
              <Sun className="w-10 h-10 text-[#C79A43] mx-auto mb-5" />

              <h2 className="text-4xl md:text-5xl font-black mb-5">
                How the birthday painting works
              </h2>

              <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
                The process is simple for the buyer, but the result feels
                layered, personal, and gift-worthy.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {howItWorks.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.65, delay: index * 0.1 }}
                  className="rounded-[2rem] border border-white/10 bg-black/45 p-7"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00B4A6] to-[#C79A43] flex items-center justify-center text-white font-black text-lg mb-6">
                    {index + 1}
                  </div>

                  <h3 className="text-2xl font-black mb-3">{item.title}</h3>
                  <p className="text-zinc-400 leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-6 py-24">
          <div className="max-w-5xl mx-auto text-center rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.025] p-8 md:p-14 shadow-2xl">
            <div className="w-16 h-16 rounded-2xl bg-[#00B4A6]/10 border border-[#00B4A6]/25 flex items-center justify-center mx-auto mb-7">
              <Sparkles className="w-8 h-8 text-[#00B4A6]" />
            </div>

            <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-6">
              Ready to turn a birthday into art?
            </h2>

            <p className="text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed mb-9">
              Order a custom birthday meaning painting for yourself or someone
              you love. A date becomes colour. A number becomes rhythm. A
              person becomes a picture.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/birthday-meaning-paintings"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-black hover:bg-zinc-200 font-black transition-all duration-300"
              >
                <ShoppingBag className="w-5 h-5" />
                Order Birthday Painting
              </Link>

              <a
                href="mailto:tartalizing@gmail.com?subject=Birthday%20Meaning%20Painting%20Enquiry"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white font-black transition-all duration-300"
              >
                <Mail className="w-5 h-5" />
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-12 border-t border-white/10 bg-black/40">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/logo.png"
                alt="Tantalizing.Art logo"
                className="w-11 h-11 rounded-full object-cover border border-white/10"
              />
              <div>
                <p className="font-black text-white">Tantalizing.Art</p>
                <p className="text-xs text-zinc-500">Trust Your Energy</p>
              </div>
            </div>

            <p className="text-zinc-500 max-w-md leading-relaxed">
              Birthday meaning paintings, daily affirmations, and creative AI
              experiences shaped around symbolism, mood, colour, and story.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-8">
            <div>
              <p className="font-black text-white mb-3">Explore</p>
              <div className="space-y-2 text-zinc-500">
                <Link
                  to="/birthday-meaning-paintings"
                  className="block hover:text-white transition"
                >
                  Birthday Paintings
                </Link>

                <Link
                  to="/music-visualizer"
                  className="block hover:text-white transition"
                >
                  Music Visualizer
                </Link>

                <Link
                  to="/mirror-of-cheer"
                  className="block hover:text-white transition"
                >
                  Mirror of Cheer
                </Link>
              </div>
            </div>

            <div>
              <p className="font-black text-white mb-3">Social</p>
              <div className="space-y-2 text-zinc-500">
                <a
                  href={YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-[#C79A43] transition"
                >
                  <PlayCircle className="w-4 h-4" />
                  YouTube
                </a>

                <a
                  href="https://www.instagram.com/arttantalizing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-pink-400 transition"
                >
                  <InstagramIcon className="w-4 h-4" />
                  Instagram
                </a>

                <a
                  href="mailto:tartalizing@gmail.com"
                  className="flex items-center gap-2 hover:text-[#00B4A6] transition"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-white/10 text-sm text-zinc-600 font-mono">
          © {new Date().getFullYear()} Tantalizing.Art • Art with a reason to exist
        </div>
      </footer>
    </div>
  );
}
